import _defineProperty from 'babel-runtime/helpers/defineProperty';
import _extends from 'babel-runtime/helpers/extends';
import PropTypes from '../_util/vue-types';
import { Provider, create } from '../_util/store';
import { default as SubPopupMenu, getActiveKey } from './SubPopupMenu';
import BaseMixin from '../_util/BaseMixin';
import hasProp, { getOptionProps, getComponentFromProp, filterEmpty, getListeners } from '../_util/props-util';
import commonPropsType from './commonPropsType';
import { getFirstFlatSubMenu, getFirstFlatSubMenuEventKeys, getFirstFlatMenuItemEventKeys, loopSubmenuRecursively } from './util';
import Trigger from '../vc-trigger';
import placements from './placements';
import { cloneElement } from '../_util/vnode';

var Menu = {
  name: 'Menu',
  props: _extends({}, commonPropsType, {
    selectable: PropTypes.bool.def(true),
    virtual: PropTypes.oneOfType([PropTypes.bool, PropTypes.object])
  }),
  mixins: [BaseMixin],

  data: function data() {
    var props = getOptionProps(this);
    var selectedKeys = props.defaultSelectedKeys;
    var openKeys = props.defaultOpenKeys;
    if ('selectedKeys' in props) {
      selectedKeys = props.selectedKeys || [];
    }
    if ('openKeys' in props) {
      openKeys = props.openKeys || [];
    }

    this.store = create({
      selectedKeys: selectedKeys,
      openKeys: openKeys,
      activeKey: {
        '0-menu-': getActiveKey(_extends({}, props, { children: this.$slots['default'] || [] }), props.activeKey)
      }
    });

    // this.isRootMenu = true // 声明在props上
    return {};
  },
  mounted: function mounted() {
    this.updateMiniStore();
  },
  updated: function updated() {
    this.updateMiniStore();
  },

  watch: {
    selectedKeys: {
      deep: true,
      handler: function handler() {
        this._preHideKey = null;
      }
    }
  },
  methods: {
    onSelect: function onSelect(selectInfo) {
      var props = this.$props;
      if (props.selectable) {
        // root menu
        var selectedKeys = this.store.getState().selectedKeys;
        var selectedKey = selectInfo.key;
        if (props.multiple) {
          selectedKeys = selectedKeys.concat([selectedKey]);
        } else {
          selectedKeys = [selectedKey];
        }
        if (!hasProp(this, 'selectedKeys')) {
          this.store.setState({
            selectedKeys: selectedKeys
          });
        }
        this.__emit('select', _extends({}, selectInfo, {
          selectedKeys: selectedKeys
        }));

        this._preHideKey = null;
      }
    },
    onClick: function onClick(e) {
      this.__emit('click', e);

      if (this.theme === 'mixed') {
        var openKeys = this.store.getState().openKeys.concat();
        if (openKeys.length > 0) {
          var firstFlatMenuItemEventKeys = getFirstFlatMenuItemEventKeys(this.$refs.innerMenu.getWrappedInstance().getFlatInstanceArray());
          if (firstFlatMenuItemEventKeys.length > 0) {
            var isFirstFlatMenuItemClick = firstFlatMenuItemEventKeys.indexOf(e.key) > -1;
            if (isFirstFlatMenuItemClick) {
              openKeys.splice(0, openKeys.length);
              if (!hasProp(this, 'openKeys')) {
                this.store.setState({ openKeys: openKeys });
              }
              this.__emit('openChange', openKeys);
            }
          }
        }
      }
    },

    // onKeyDown needs to be exposed as a instance method
    // e.g., in rc-select, we need to navigate menu item while
    // current active item is rc-select input box rather than the menu itself
    onKeyDown: function onKeyDown(e, callback) {
      this.$refs.innerMenu.getWrappedInstance().onKeyDown(e, callback);
    },
    onOpenChange: function onOpenChange(event) {
      var _this = this;

      var openKeys = this.store.getState().openKeys.concat();
      var changed = false;
      var processSingle = function processSingle(e) {
        var oneChanged = false;
        if (e.open && !(_this._preHideKey && _this._preHideKey === e.key)) {
          oneChanged = openKeys.indexOf(e.key) === -1;
          if (oneChanged) {
            if (_this.theme === 'mixed') {
              var flatFirstSubMenuEventKeys = getFirstFlatSubMenuEventKeys(_this.$refs.innerMenu.getWrappedInstance().getFlatInstanceArray());
              // 如果将要展示的是副导航
              if (flatFirstSubMenuEventKeys.indexOf(e.key) > -1) {
                // 把其他副导航隐藏
                openKeys = openKeys.filter(function (k) {
                  return flatFirstSubMenuEventKeys.indexOf(k) === -1;
                });
              }
            }

            openKeys.push(e.key);
          }
        } else {
          var index = openKeys.indexOf(e.key);
          oneChanged = index !== -1;
          if (oneChanged) {
            openKeys.splice(index, 1);
          }
        }
        changed = changed || oneChanged;
      };
      if (Array.isArray(event)) {
        // batch change call
        event.forEach(processSingle);
      } else {
        processSingle(event);
      }
      if (changed) {
        if (!this._preHideKey && this.theme === 'mixed') {
          var firstFlatSubMenu = getFirstFlatSubMenu(this.$refs.innerMenu.getWrappedInstance().getFlatInstanceArray());
          var flatFirstSubMenuEventKeys = firstFlatSubMenu.map(function (_ref) {
            var eventKey = _ref.eventKey;
            return eventKey;
          });
          if (flatFirstSubMenuEventKeys.length > 0) {
            // 查看是否还有副导航在展示
            var isSomeMenuOpen = flatFirstSubMenuEventKeys.some(function (k) {
              return openKeys.indexOf(k) > -1;
            });
            if (!isSomeMenuOpen) {
              // 没有的话，查看是否有选中的副导航
              var selectedSubmenuArray = firstFlatSubMenu.filter(function (instance) {
                return instance.isChildrenSelected && instance.isChildrenSelected();
              });
              if (selectedSubmenuArray.length > 0) {
                // 展示选中的副导航
                var eventKey = selectedSubmenuArray[0].eventKey;

                openKeys.push(eventKey);
              }
            }
          }
        }

        if (!hasProp(this, 'openKeys')) {
          this.store.setState({ openKeys: openKeys });
        }
        this.__emit('openChange', openKeys);
      }
    },
    onDeselect: function onDeselect(selectInfo) {
      var props = this.$props;
      if (props.selectable) {
        var selectedKeys = this.store.getState().selectedKeys.concat();
        var selectedKey = selectInfo.key;
        var index = selectedKeys.indexOf(selectedKey);
        if (index !== -1) {
          selectedKeys.splice(index, 1);
        }
        if (!hasProp(this, 'selectedKeys')) {
          this.store.setState({
            selectedKeys: selectedKeys
          });
        }
        this.__emit('deselect', _extends({}, selectInfo, {
          selectedKeys: selectedKeys
        }));
      }
    },
    getOpenTransitionName: function getOpenTransitionName() {
      var props = this.$props;
      var transitionName = props.openTransitionName;
      var animationName = props.openAnimation;
      if (!transitionName && typeof animationName === 'string' && this.theme !== 'mixed') {
        transitionName = props.prefixCls + '-open-' + animationName;
      }
      return transitionName;
    },
    updateMiniStore: function updateMiniStore() {
      var props = getOptionProps(this);
      if ('selectedKeys' in props) {
        this.store.setState({
          selectedKeys: props.selectedKeys || []
        });
      }
      if ('openKeys' in props) {
        this.store.setState({
          openKeys: props.openKeys || []
        });
      }
    },
    isSubMenuSelected: function isSubMenuSelected() {
      var ret = { find: false };
      loopSubmenuRecursively(this.$slots['default'], this.$props.selectedKeys || [], ret);
      return ret.find;
    },
    onExpandOpenBtnClickHide: function onExpandOpenBtnClickHide() {
      this.onExpandOpenBtnClick({ open: true });
    },
    onExpandOpenBtnClick: function onExpandOpenBtnClick(e) {
      var openKeys = this.store.getState().openKeys.concat();
      if (e.open) {
        if (this._preHideKey) {
          openKeys.push(this._preHideKey);
          this._preHideKey = null;
        }
      } else {
        this._preHideKey = e.key;

        var index = openKeys.indexOf(e.key);
        if (index > -1) {
          openKeys.splice(index, 1);
        }
      }

      if (!hasProp(this, 'openKeys')) {
        this.store.setState({ openKeys: openKeys });
      }
      this.__emit('openChange', openKeys);
    }
  },

  render: function render() {
    var h = arguments[0];

    var props = getOptionProps(this);

    var subPopupMenuProps = {
      props: _extends({}, props, {
        itemIcon: getComponentFromProp(this, 'itemIcon', props),
        expandIcon: getComponentFromProp(this, 'expandIcon', props),
        overflowedIndicator: getComponentFromProp(this, 'overflowedIndicator', props) || h('span', ['\xB7\xB7\xB7']),
        openTransitionName: this.getOpenTransitionName(),
        parentMenu: this,
        children: filterEmpty(this.$slots['default'] || [])
      }),
      'class': _defineProperty({}, props.prefixCls + '-root', true),
      on: _extends({}, getListeners(this), {
        click: this.onClick,
        openChange: this.onOpenChange,
        deselect: this.onDeselect,
        select: this.onSelect
      }),
      ref: 'innerMenu'
    };

    if (this.theme === 'mixed') {
      var builtinPlacements = _extends({}, placements, this.theme === 'mixed' ? {
        rightCenter: {
          points: ['cl', 'cr'],
          overflow: {
            adjustX: 1,
            adjustY: 1
          },
          offset: [0, 0]
        }
      } : {}, props.builtinPlacements);
      var expandOpenBtn = getComponentFromProp(this, 'expandOpenBtn', props);
      subPopupMenuProps.props.expandOpenBtn = expandOpenBtn;
      subPopupMenuProps['class'][props.prefixCls + '-has-submenu-selected-open'] = this.isSubMenuSelected() && !this._preHideKey;
      subPopupMenuProps.on.expandOpenBtnClick = this.onExpandOpenBtnClick;

      var expandOpenBtnHide = cloneElement(expandOpenBtn, {
        on: {
          click: this.onExpandOpenBtnClickHide
        }
      });

      return h(
        Provider,
        {
          attrs: { store: this.store }
        },
        [h(
          Trigger,
          {
            attrs: {
              prefixCls: props.prefixCls + '-expand-open-btn-popup',
              popupVisible: Boolean(this._preHideKey),
              popupClassName: props.prefixCls + '-' + props.theme,
              getPopupContainer: props.getPopupContainer,
              builtinPlacements: builtinPlacements,
              popupPlacement: 'rightCenter',
              popupAlign: {}
            }
          },
          [h(SubPopupMenu, subPopupMenuProps), h(
            'template',
            { slot: 'popup' },
            [expandOpenBtnHide]
          )]
        )]
      );
    }

    return h(
      Provider,
      {
        attrs: { store: this.store }
      },
      [h(SubPopupMenu, subPopupMenuProps)]
    );
  }
};
export default Menu;